/**
 * YagiChatBot の質問フロー（skip / submit / 次質問）。
 */
import { nextTick, type ComputedRef, type Ref } from 'vue'
import {
  fieldValueAsString,
  isFilledString,
  type YagiChatFormValue,
  type YagiChatMessage,
  type YagiChatQuestion,
  type YagiChatQuickReply
} from '@/utils/format-edit/yagiChatBot'

export type YagiChatQuestionsDeps = {
  questions: ComputedRef<YagiChatQuestion[]>
  value: ComputedRef<YagiChatFormValue>
  emitInput: (value: YagiChatFormValue) => void
  emitComplete: (value?: YagiChatFormValue) => void
  currentQuestionIndex: Ref<number>
  chatMessages: Ref<YagiChatMessage[]>
  currentAnswer: Ref<string>
  autoFilledFields: Ref<string[]>
  allQuestionsCompleted: Ref<boolean>
  aiResultMode: Ref<boolean>
  aiResultCallbacks: Ref<unknown>
  thinkingMode: Ref<boolean>
  scrollChatToBottom: () => void
}

export function useYagiChatBotQuestions(deps: YagiChatQuestionsDeps) {
  function addQuestionToChat(index: number) {
    if (index >= deps.questions.value.length) return
    const question = deps.questions.value[index]
    deps.chatMessages.value.push({
      type: 'question',
      text: question.question,
      description: question.description,
      placeholder: question.placeholder,
      questionId: question.id
    })
    nextTick(() => deps.scrollChatToBottom())
  }

  function skipFilledQuestions() {
    let skippedCount = 0
    const skippedQuestions: string[] = []
    const skippedIndices: number[] = []

    while (deps.currentQuestionIndex.value < deps.questions.value.length) {
      const question = deps.questions.value[deps.currentQuestionIndex.value]

      if (question.skipIf && question.skipIf(deps.value.value)) {
        deps.currentQuestionIndex.value++
        continue
      }

      const fieldVal = deps.value.value[question.field]
      if (isFilledString(fieldVal)) {
        const displayText = question.question.replace(/を教えてください$/, '')
        skippedQuestions.push(displayText)
        skippedIndices.push(deps.currentQuestionIndex.value)
        deps.currentQuestionIndex.value++
        skippedCount++
      } else {
        break
      }
    }

    if (skippedCount > 0) {
      deps.chatMessages.value.push({
        type: 'system',
        text: `履歴書から${skippedCount}件の情報を取得しました`,
        skippedQuestions,
        skippedIndices
      })
    }

    if (deps.currentQuestionIndex.value < deps.questions.value.length) {
      addQuestionToChat(deps.currentQuestionIndex.value)
      const currentQuestion =
        deps.questions.value[deps.currentQuestionIndex.value]
      deps.currentAnswer.value = fieldValueAsString(
        deps.value.value,
        currentQuestion.field
      )
    }

    nextTick(() => deps.scrollChatToBottom())
  }

  function moveToNextUnskippedQuestion(filledFields: string[]) {
    while (deps.currentQuestionIndex.value < deps.questions.value.length) {
      const question = deps.questions.value[deps.currentQuestionIndex.value]
      if (question.autoSkipIfFilled && filledFields.includes(question.field)) {
        deps.currentQuestionIndex.value++
      } else {
        break
      }
    }

    if (deps.currentQuestionIndex.value < deps.questions.value.length) {
      addQuestionToChat(deps.currentQuestionIndex.value)
      const nextQuestion = deps.questions.value[deps.currentQuestionIndex.value]
      deps.currentAnswer.value = fieldValueAsString(
        deps.value.value,
        nextQuestion.field
      )
    } else {
      deps.emitComplete()
      if (!deps.thinkingMode.value) {
        deps.allQuestionsCompleted.value = true
      }
    }
  }

  function moveToNextQuestion(latestValue?: YagiChatFormValue) {
    const valueForSkip = latestValue || deps.value.value

    while (
      deps.currentQuestionIndex.value < deps.questions.value.length &&
      deps.questions.value[deps.currentQuestionIndex.value].skipIf &&
      deps.questions.value[deps.currentQuestionIndex.value].skipIf!(
        valueForSkip
      )
    ) {
      deps.currentQuestionIndex.value++
    }

    if (deps.autoFilledFields.value.length > 0) {
      moveToNextUnskippedQuestion(deps.autoFilledFields.value)
    } else if (deps.currentQuestionIndex.value < deps.questions.value.length) {
      addQuestionToChat(deps.currentQuestionIndex.value)
      const nextQuestion = deps.questions.value[deps.currentQuestionIndex.value]
      deps.currentAnswer.value =
        nextQuestion && !nextQuestion.quickReplies
          ? fieldValueAsString(valueForSkip, nextQuestion.field)
          : ''
    } else {
      deps.emitComplete()
      if (!deps.thinkingMode.value) {
        deps.allQuestionsCompleted.value = true
      }
    }

    nextTick(() => deps.scrollChatToBottom())
  }

  function submitAnswer() {
    const currentQuestion =
      deps.questions.value[deps.currentQuestionIndex.value]
    const updatedValue = {
      ...deps.value.value,
      [currentQuestion.field]: deps.currentAnswer.value
    }
    deps.emitInput(updatedValue)

    if (deps.currentAnswer.value && deps.currentAnswer.value.trim()) {
      deps.chatMessages.value.push({
        type: 'answer',
        text: deps.currentAnswer.value
      })
    }

    deps.currentQuestionIndex.value++
    deps.currentAnswer.value = ''
    moveToNextQuestion(updatedValue)
  }

  function submitNoneAnswer() {
    const currentQuestion =
      deps.questions.value[deps.currentQuestionIndex.value]
    const updatedValue = {
      ...deps.value.value,
      [currentQuestion.field]: 'なし'
    }
    deps.emitInput(updatedValue)

    deps.chatMessages.value.push({
      type: 'answer',
      text: 'なし'
    })

    deps.currentQuestionIndex.value++
    deps.currentAnswer.value = ''
    moveToNextQuestion(updatedValue)
  }

  function submitQuickReply(reply: YagiChatQuickReply) {
    const currentQuestion =
      deps.questions.value[deps.currentQuestionIndex.value]
    const updatedValue = {
      ...deps.value.value,
      [currentQuestion.field]: reply.value
    }
    deps.emitInput(updatedValue)

    deps.chatMessages.value.push({
      type: 'answer',
      text: reply.label
    })

    deps.currentQuestionIndex.value++
    deps.currentAnswer.value = ''
    moveToNextQuestion(updatedValue)
  }

  function editSkippedQuestions(skippedIndices: number[]) {
    if (!skippedIndices || skippedIndices.length === 0) return
    deps.autoFilledFields.value = []
    deps.aiResultMode.value = false
    deps.aiResultCallbacks.value = null
    deps.currentAnswer.value = ''
    deps.currentQuestionIndex.value = skippedIndices[0]
    deps.chatMessages.value = []
    addQuestionToChat(deps.currentQuestionIndex.value)
    const currentQuestion =
      deps.questions.value[deps.currentQuestionIndex.value]
    deps.currentAnswer.value = fieldValueAsString(
      deps.value.value,
      currentQuestion.field
    )
  }

  function handleQuestionsComplete() {
    deps.emitComplete(deps.value.value)
  }

  return {
    addQuestionToChat,
    skipFilledQuestions,
    moveToNextQuestion,
    moveToNextUnskippedQuestion,
    submitAnswer,
    submitNoneAnswer,
    submitQuickReply,
    editSkippedQuestions,
    handleQuestionsComplete
  }
}
